import { faSpinner, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as d3 from "d3";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GroupedBySiteType } from "../../../apiClient/generated";
import { useResizeObserver } from "../../../ui/Plot/hooks";
import type { AnalyticsFilters } from "../state";
import { BAR_COLOR, BAR_HIGHLIGHT_COLOR } from "../utils/plotColors";
import { renderPlotHeader } from "../utils/plotUtils";

interface PercentagePlotProps {
    svgRef?: React.MutableRefObject<SVGSVGElement | null>;
    emissionsData: GroupedBySiteType[];
    emissionsBelowThreshold: GroupedBySiteType[];
    nonDetectsData?: GroupedBySiteType[];
    loading?: boolean;
    filters: AnalyticsFilters;
}

export const PercentagePlot = ({
    svgRef: externalSvgRef,
    emissionsData,
    emissionsBelowThreshold,
    nonDetectsData,
    loading,
    filters,
}: PercentagePlotProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const dimensions = useResizeObserver(containerRef);

    // Sync internal ref to external ref for image export
    useEffect(() => {
        if (externalSvgRef && svgRef.current) {
            externalSvgRef.current = svgRef.current;
        }
    }, [externalSvgRef, dimensions]);

    // Zoom state and refs
    const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(
        d3.zoomIdentity,
    );
    const [zoomDomain, setZoomDomain] = useState<{
        x: [number, number] | null;
        y: [number, number] | null;
    }>({
        x: null,
        y: null,
    });
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();

    // Reset handler
    const handleReset = useCallback(() => {
        // Reset both zoom transform and domain
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);

        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current)
            .transition()
            .duration(750)
            .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }, []);

    const transformedData = useMemo(() => {
        if (!emissionsData || !nonDetectsData || !emissionsBelowThreshold)
            return [];

        return emissionsData
            .map((emissionData) => {
                const nonDetectsCount =
                    nonDetectsData.find(
                        (n) => n.siteType === emissionData.siteType,
                    )?.count || 0;
                const emissionsBelowThresholdCount =
                    emissionsBelowThreshold.find(
                        (n) => n.siteType === emissionData.siteType,
                    )?.count || 0;
                return {
                    siteType: emissionData.siteType || "Other",
                    emissionsCount: emissionData.count,
                    nonDetectsCount,
                    emissionsBelowThresholdCount,
                    percentage:
                        (emissionData.count /
                            (emissionData.count +
                                nonDetectsCount +
                                emissionsBelowThresholdCount)) *
                        100,
                };
            })
            .toSorted((a, b) => b.percentage - a.percentage);
    }, [emissionsData, nonDetectsData, emissionsBelowThreshold]);

    // Setup zoom and brush behaviors (runs when dimensions change)
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Create zoom behavior (double-click only)
        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, 10])
            .on("zoom", (event) => {
                setCurrentTransform(event.transform);
            })
            .filter((event) => {
                // Only allow  double-click, disable drag (drag is for brush)
                return event.type === "dblclick";
            });

        zoomBehaviorRef.current = zoom;

        // Apply zoom to SVG
        svg.call(zoom);

        // Setup double-click to reset
        svg.on("dblclick.zoom", () => {
            setZoomDomain({ x: null, y: null });
            setCurrentTransform(d3.zoomIdentity);
            svg.transition()
                .duration(750)
                .call(zoom.transform, d3.zoomIdentity);
        });
    }, [dimensions]);

    // Reset zoom when data changes
    useEffect(() => {
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current).call(
            zoomBehaviorRef.current.transform,
            d3.zoomIdentity,
        );
    }, [transformedData]);

    // Effect 3: Render chart (runs on data/transform/dimension changes)
    useEffect(() => {
        if (!transformedData || transformedData.length === 0) return;
        if (dimensions.width === 0 || dimensions.height === 0) return;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        const margin = { top: 70, right: 10, bottom: 45, left: 75 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Draw header
        renderPlotHeader({
            svg: svg,
            plotTitle: `Observations Exceeding ${filters.detectionLimit.toFixed(0)} kg/h By Site Type`,
            filters,
        });

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Filter data based on box zoom domain
        let visibleData = transformedData;
        if (zoomDomain.x) {
            const allSiteTypes = transformedData.map((d) => d.siteType);
            const startIdx = Math.floor(zoomDomain.x[0]);
            const endIdx = Math.ceil(zoomDomain.x[1]);
            const visibleSiteTypes = allSiteTypes.slice(
                Math.max(0, startIdx),
                Math.min(allSiteTypes.length, endIdx),
            );
            visibleData = transformedData.filter((d) =>
                visibleSiteTypes.includes(d.siteType),
            );
        }

        // Create X scale with zoom transform applied to range
        const xScale = d3
            .scaleBand()
            .domain(visibleData.map((d) => d.siteType))
            .range([0, width].map((d) => currentTransform.applyX(d)))
            .padding(0.2);

        // Create Y scale with box zoom domain and 0% baseline constraint
        const yMax = d3.max(visibleData, (v) => v.percentage) || 100;
        let yDomain: [number, number] = [0, yMax];

        if (zoomDomain.y) {
            // Apply box zoom domain, but enforce 0 baseline
            yDomain = [0, Math.max(zoomDomain.y[1], 1)];
        }

        const yScale = d3
            .scaleLinear()
            .domain(yDomain)
            .range([height, 0].map((d) => currentTransform.applyY(d)));

        // Ensure 0% is always visible by clamping the range
        const yZero = yScale(0);
        if (yZero > height) {
            // Adjust scale to keep 0 visible
            const adjustment = yZero - height;
            yScale.range([height, yScale.range()[1] - adjustment]);
        }

        // Create brush for box zoom
        const brush = d3
            .brush()
            .extent([
                [0, 0],
                [width, height],
            ])
            .on("end", (event) => {
                if (!event.selection) return;

                const [[x0, y0], [x1, y1]] = event.selection as [
                    [number, number],
                    [number, number],
                ];

                // Ignore very small selections
                if (Math.abs(x1 - x0) < 10 || Math.abs(y1 - y0) < 10) {
                    chart.select<SVGGElement>(".brush").call(brush.move, null);
                    return;
                }

                // Convert pixel coordinates to data domain
                // For X: map to site type indices
                // Get current zoom range (or full range if not zoomed)
                const currentStart = zoomDomain.x ? zoomDomain.x[0] : 0;
                const currentEnd = zoomDomain.x
                    ? zoomDomain.x[1]
                    : transformedData.length;
                const currentRange = currentEnd - currentStart;

                // Calculate brush selection as fraction of visible area
                const startFraction = x0 / width;
                const endFraction = x1 / width;

                // Map to absolute indices in transformedData
                const absoluteStart =
                    currentStart + currentRange * startFraction;
                const absoluteEnd = currentStart + currentRange * endFraction;

                const xDomain: [number, number] = [
                    Math.max(0, Math.floor(absoluteStart)),
                    Math.min(transformedData.length, Math.ceil(absoluteEnd)),
                ];

                // For Y: invert scale (SVG y-axis is inverted)
                const yDomain: [number, number] = [
                    Math.max(0, yScale.invert(y1)),
                    yScale.invert(y0),
                ];

                // Apply constraint: always include 0 in Y domain
                yDomain[0] = 0;

                setZoomDomain({ x: xDomain, y: yDomain });

                // Clear brush selection after zoom
                chart.select<SVGGElement>(".brush").call(brush.move, null);
            });

        // Attach brush to chart
        const brushGroup = chart.append("g").attr("class", "brush").call(brush);

        // Style brush selection
        brushGroup
            .select(".selection")
            .attr("fill", "#1677ff")
            .attr("fill-opacity", 0.2)
            .attr("stroke", "#1677ff")
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "4,4");

        // Draw Y-axis gridlines
        chart
            .append("g")
            .attr("class", "y-grid")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .selectAll("line")
                    .attr("stroke", "#ccc")
                    .attr("stroke-opacity", 0.15)
                    .attr("shape-rendering", "crispEdges"),
            );

        // Filter data to only visible items (within viewport)
        const visibleFilteredData = visibleData.filter((d) => {
            const x = xScale(d.siteType);
            if (x === undefined) return false;
            const barWidth = xScale.bandwidth();
            // Keep bar if any part of it is visible
            return x + barWidth >= 0 && x <= width;
        });

        const barGroups = chart
            .selectAll(".bar-group")
            .data(visibleFilteredData)
            .join("g")
            .attr("class", "bar-group")
            .attr("transform", (d) => `translate(${xScale(d.siteType) || 0},0)`)
            .on("mouseenter", function () {
                d3.select(this)
                    .select("rect.bar")
                    .attr("fill", BAR_HIGHLIGHT_COLOR);
            })
            .on("mouseleave", function () {
                d3.select(this).select("rect.bar").attr("fill", BAR_COLOR);
            });

        barGroups
            .append("rect")
            .attr("class", "bar")
            .attr("x", 0)
            .attr("y", (d) => yScale(d.percentage))
            .attr("width", xScale.bandwidth())
            .attr("height", (d) => height - yScale(d.percentage))
            .attr("fill", BAR_COLOR);

        barGroups
            .append("text")
            .attr("class", "label")
            .attr("x", xScale.bandwidth() / 2)
            .attr("y", (d) => yScale(d.percentage) - 5)
            .attr("text-anchor", "middle")
            .attr("font-size", "11px")
            .attr("font-family", "sans-serif")
            .attr("fill", "#333")
            .text((d) => `${d.percentage.toFixed(2)}%`);

        // Draw X-axis
        chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xScale))
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .each(function () {
                const text = d3.select(this);
                const words = text.text().split("_");
                text.text(null);

                const midpoint = Math.ceil(words.length / 2);

                // Capitalize manually since applying styles is not working here.
                const line1 = words
                    .slice(0, midpoint)
                    .map(
                        (w: string) =>
                            w.charAt(0).toUpperCase() +
                            w.slice(1).toLowerCase(),
                    )
                    .join(" ");
                const line2 = words
                    .slice(midpoint)
                    .map(
                        (w: string) =>
                            w.charAt(0).toUpperCase() +
                            w.slice(1).toLowerCase(),
                    )
                    .join(" ");

                text.append("tspan")
                    .attr("x", 0)
                    .attr("dy", 7)
                    .style("text-transform", "capitalize")
                    .text(line1);

                text.append("tspan")
                    .attr("x", 0)
                    .attr("dy", "1.1em")
                    .style("text-transform", "capitalize")
                    .text(line2);
            });

        // Draw Y-axis
        chart
            .append("g")
            .attr("class", "y-axis")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickFormat((d) => `${d}%`),
            )
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px");

        // Add y-axis label
        chart
            .append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, -55)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .text("Observations Over Threshold (%)");

        // Add x-axis label
        chart
            .append("text")
            .attr("x", width / 2)
            .attr("y", height + 42)
            .attr("fill", "#666")
            .attr("text-anchor", "middle")
            .attr("font-size", "12px")
            .text("Site Types");
    }, [dimensions, transformedData, currentTransform, zoomDomain, filters]);

    return (
        <div
            ref={containerRef}
            className="relative flex-grow w-full h-full overflow-hidden"
        >
            <svg
                ref={svgRef}
                className="w-full h-full"
                style={{ cursor: "crosshair" }}
            />
            {/* Reset zoom button - only show when zoomed */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null) && (
                <button
                    type="button"
                    onClick={handleReset}
                    className="absolute top-11 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}
            {/* Loading overlay */}
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                    />
                    <div className="text-gray-500">Loading...</div>
                </div>
            )}
            {/* Empty data overlay */}
            {!loading && transformedData.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                    <FontAwesomeIcon icon={faWarning} />
                    <div className="text-gray-500">
                        No data available for selected filters
                    </div>
                </div>
            )}
        </div>
    );
};
